import dayjs from "dayjs";
import { getLabtestsList, getAllLabTests } from "../../services/labtestService";
import { dedupePatientLabReportRows, dedupePathLabReportRecords } from "../../utils/labTestDedupeUtils";
import {
  getAppointmentStatusUpper,
  getPaymentMethodUpper,
  getTokenStatusUpper,
  needsFrontdeskConfirmation,
  hasAppointmentToken,
  isAppointmentCancelled,
  isAppointmentCompleted,
  isAppointmentRejected,
  isBlockingSameDayAppointment,
} from "../../utils/appointmentCancelUtils";

const normalizeLabTestList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.content)) return data.content;
  return [];
};

export const getLabTestDisplayName = (test) =>
  test?.name || test?.testName || test?.test_name || test?.labTestName || "";

/** Remove duplicate lab tests (same id or same display name). */
export const dedupeBookableLabTests = (tests) => {
  const list = Array.isArray(tests) ? tests : [];
  const result = [];
  const seenIds = new Set();
  const seenNames = new Set();

  list.forEach((test) => {
    const nameKey = getLabTestDisplayName(test).trim().toLowerCase();
    if (!nameKey) return;

    const id = test?.id;
    if (id != null && seenIds.has(id)) return;
    if (seenNames.has(nameKey)) return;

    if (id != null) seenIds.add(id);
    seenNames.add(nameKey);
    result.push(test);
  });

  return result;
};

/** Load bookable lab tests (org-scoped first, then base table fallback). */
export const fetchPatientBookableLabTests = async () => {
  const filterActive = (tests) =>
    tests.filter(
      (t) => t?.is_active !== 0 && t?.isActive !== false && t?.active !== false
    );

  try {
    let tests = normalizeLabTestList(await getLabtestsList());
    if (tests.length === 0) {
      tests = normalizeLabTestList(await getAllLabTests());
    }
    return dedupeBookableLabTests(filterActive(tests));
  } catch (orgError) {
    console.warn(
      "[patientDashboard] Org lab tests unavailable, trying base table:",
      orgError
    );
    try {
      return dedupeBookableLabTests(
        filterActive(normalizeLabTestList(await getAllLabTests()))
      );
    } catch (baseError) {
      console.error("[patientDashboard] Failed to load lab tests:", baseError);
      return [];
    }
  }
};

const readPrescribedLabTestStatus = (test) =>
  String(test?.resultStatus ?? test?.result_status ?? test?.status ?? "")
    .trim()
    .toUpperCase();

const prescribedLabTestHasResult = (test) => {
  const result = test?.result;
  if (result == null) return false;
  const text = String(result).trim().toLowerCase();
  return text !== "" && text !== "null" && text !== "—";
};

/** completed | in-progress | rejected | bookable */
export const classifyPrescribedLabTestForBooking = (test) => {
  const status = readPrescribedLabTestStatus(test);
  if (status === "COMPLETE" || status === "COMPLETED") return "completed";
  if (!status && prescribedLabTestHasResult(test)) return "completed";
  if (status === "BILLED" || status === "COLLECTED") return "in-progress";
  if (status === "REJECT" || status === "REJECTED") return "rejected";
  if (!status || status === "PENDING") return "bookable";
  return "in-progress";
};

/** Prescription payload for patient self-booked lab tests (creates patient_pathology on backend). */
export const normalizePrescribedLabTests = (prescription) => {
  const prescribed = Array.isArray(prescription?.patientLabTests)
    ? prescription.patientLabTests.filter((test) => test?.labTestId != null)
    : [];
  return prescribed.map((test) => {
    const bookingState = classifyPrescribedLabTestForBooking(test);
    return {
      id: test.labTestId,
      labTestId: test.labTestId,
      name: test.testName || test.labTestName || `Lab Test #${test.labTestId}`,
      testName: test.testName || test.labTestName || `Lab Test #${test.labTestId}`,
      resultStatus: readPrescribedLabTestStatus(test) || null,
      result: test.result ?? null,
      bookable: bookingState === "bookable",
      bookingState,
    };
  });
};

export const validatePrescriptionForLabBooking = (prescription, patientId) => {
  const prescriptionPatientId = prescription?.patientId ?? prescription?.patient_id;
  if (!prescription || Number(prescriptionPatientId) !== Number(patientId)) {
    return {
      ok: false,
      message: "This prescription was not found for your patient account.",
    };
  }
  const prescribedTests = normalizePrescribedLabTests(prescription);
  if (prescribedTests.length === 0) {
    return {
      ok: false,
      message: "No lab tests were prescribed on this prescription. Test booking is not allowed.",
      prescribedTests: [],
      tests: [],
    };
  }
  const prescribedAt = readPrescribedAt(prescription);
  if (prescribedAt) {
    const opensAt = prescribedAt.add(1, "day");
    if (dayjs().isBefore(opensAt)) {
      return {
        ok: false,
        message:
          "You can book this test only after 1 day has passed from when the doctor prescribed it. Booking opens on {date}.",
        messageVars: { date: opensAt.format("DD MMM YYYY, hh:mm A") },
      };
    }
  }
  const tests = prescribedTests.filter((test) => test.bookable);
  const prescriptionId =
    prescription.id ?? prescription.prescriptionId ?? prescription.prescription_id;
  if (tests.length === 0) {
    return {
      ok: false,
      message: "Completed tests do not need to be booked again. Only pending tests can be booked.",
      prescribedTests,
      tests: [],
      prescriptionId,
    };
  }
  return { ok: true, tests, prescribedTests, prescriptionId };
};

export const describeLabBookingCheck = (check) => {
  let text = String(check?.message || "");
  const vars = check?.messageVars;
  if (vars) {
    Object.entries(vars).forEach(([name, value]) => {
      text = text.replace(new RegExp(`\\{${name}\\}`, "g"), String(value ?? ""));
    });
  }
  return text;
};

const readPrescribedAt = (prescription) => {
  const prescriptionValues = [
    prescription?.createdAt,
    prescription?.created_at,
    prescription?.prescriptionDate,
    prescription?.prescription_date,
    prescription?.date,
  ];
  for (const value of prescriptionValues) {
    const parsed = parseDashboardLabDate(value);
    if (parsed) return parsed;
  }
  const testDates = (Array.isArray(prescription?.patientLabTests) ? prescription.patientLabTests : [])
    .map((test) =>
      parseDashboardLabDate(test?.createdAt ?? test?.created_at ?? test?.prescribedAt ?? test?.prescribed_at)
    )
    .filter(Boolean);
  if (!testDates.length) return null;
  return testDates.reduce((latest, current) => (current.isAfter(latest) ? current : latest));
};

export const buildPatientSelfBookLabTestPayload = (
  patientId,
  selectedTests,
  doctorId
) => ({
  patientId: Number(patientId),
  doctorId: Number(doctorId),
  complaints: ["Patient portal lab test booking"],
  diagnoses: [],
  medicines: [],
  vitalSigns: [],
  patientLabTests: (Array.isArray(selectedTests) ? selectedTests : [])
    .map((test) => ({ labTestId: test?.id }))
    .filter((entry) => entry.labTestId != null),
  nextVisitDate: null,
  isExtraMedicine: false,
  numberOfDays: 0,
  deductInventory: false,
});

export const isPendingPatientBooking = (appointmentData) =>
  needsFrontdeskConfirmation(appointmentData);

/** Completed, cancelled, and rejected visits belong in previous consultations. */
export const isClosedPatientAppointment = (appointment) => {
  const data = appointment?.appointmentData || appointment;
  if (!data) return false;
  return (
    isAppointmentCancelled(data) ||
    isAppointmentRejected(data) ||
    isAppointmentCompleted(data)
  );
};

export const canPatientCancelAppointment = (enriched) => {
  const data = enriched?.appointmentData || enriched;
  if (!data || data.isCancel === true) return false;

  const status = getAppointmentStatusUpper(data);
  const tokenStatus = getTokenStatusUpper(data);
  if (status === "COMPLETED" || tokenStatus === "COMPLETED") return false;
  if (status === "REJECTED" || status === "CANCELLED" || status === "CANCELED") {
    return false;
  }

  if (isPendingPatientBooking(data)) return true;

  const when = enriched?.when;
  return Boolean(when?.isValid?.() && when.isAfter(dayjs()));
};

export const canPatientRescheduleAppointment = (enriched) => {
  const data = enriched?.appointmentData || enriched;
  if (!data || data.isCancel === true) return false;

  const status = getAppointmentStatusUpper(data);
  const tokenStatus = getTokenStatusUpper(data);
  if (status === "COMPLETED" || tokenStatus === "COMPLETED") return false;
  if (status === "REJECTED" || status === "CANCELLED" || status === "CANCELED") {
    return false;
  }

  const when = enriched?.when;
  if (!when?.isValid?.()) return isPendingPatientBooking(data);

  if (isPendingPatientBooking(data)) return true;

  if (when.isAfter(dayjs())) {
    return (
      status === "CONFIRMED" ||
      status === "SCHEDULED" ||
      status === "ACTIVE" ||
      !status
    );
  }

  return false;
};

export const getDisplayAppointmentStatus = (enriched) => {
  const data = enriched?.appointmentData || enriched;
  const status = getAppointmentStatusUpper(data);
  if (status === "PENDING_CONFIRMATION" || status === "PENDING") {
    return "Pending Confirmation";
  }
  return enriched?.status || status || "Scheduled";
};

/** Build update payload for patient edit / reschedule (org-scoped patient id + correct status). */
export const buildPatientAppointmentUpdatePayload = ({
  patientId,
  doctorId,
  appointmentDateTime,
  isVirtual = false,
  existingData = {},
}) => {
  const paymentMethodValue =
    existingData.paymentMethod ||
    existingData.payment_method ||
    (getPaymentMethodUpper(existingData) === "PAID_ONLINE"
      ? "PAID_ONLINE"
      : "PAY_AT_HOSPITAL");

  const existingStatus = getAppointmentStatusUpper(existingData);

  let status;
  if (needsFrontdeskConfirmation(existingData)) {
    status = "PENDING_CONFIRMATION";
  } else if (existingStatus === "CONFIRMED") {
    status = "CONFIRMED";
  } else if (
    paymentMethodValue === "PAY_AT_HOSPITAL" &&
    !hasAppointmentToken(existingData)
  ) {
    status = "PENDING_CONFIRMATION";
  } else {
    status = existingStatus || "SCHEDULED";
  }

  return {
    patientId: Number(patientId),
    doctorId: Number(doctorId),
    appointmentDateTime,
    isVirtual: Boolean(isVirtual),
    paymentMethod: paymentMethodValue,
    status,
    isCancel: false,
  };
};

export const getAppointmentIdFromEnriched = (enriched) => {
  const data = enriched?.appointmentData || enriched;
  const id = data?.id ?? enriched?.id ?? data?.appointmentId ?? data?.appointment_id;
  if (id == null || id === "") return null;
  const numeric = Number(id);
  return Number.isNaN(numeric) ? id : numeric;
};

export const isAlreadyBookedAtSelectedTimeError = (error) => {
  const message = String(
    error?.response?.data?.message || error?.message || ""
  ).toLowerCase();
  return message.includes("already have an appointment");
};

export const getRawAppointmentDateTime = (appointment) =>
  appointment?.appointmentDateTime ||
  appointment?.appointment_date_time ||
  appointment?.appointmentDate ||
  appointment?.appointment_date ||
  null;

/** Pending visit at the exact selected date and time. */
export const findPatientSameDoctorDayConflict = ({
  appointments,
  patientId,
  appointmentDateTime,
}) => {
  const list = Array.isArray(appointments) ? appointments : [];
  if (!appointmentDateTime || !dayjs(appointmentDateTime).isValid()) return null;

  return (
    list.find((apt) => {
      const aptPatientId = apt?.patientId ?? apt?.patient_id;
      if (
        aptPatientId != null &&
        patientId != null &&
        String(aptPatientId) !== String(patientId)
      ) {
        return false;
      }
      if (!isBlockingSameDayAppointment(apt)) return false;
      const raw = getRawAppointmentDateTime(apt);
      return (
        raw &&
        dayjs(raw).isValid() &&
        dayjs(raw).isSame(dayjs(appointmentDateTime), "minute")
      );
    }) || null
  );
};

const getPrescriptionAppointmentId = (prescription) => {
  const id = prescription?.appointmentId ?? prescription?.appointment_id;
  if (id == null || id === "") return null;
  const numeric = Number(id);
  return Number.isNaN(numeric) ? id : numeric;
};

const getPrescriptionId = (prescription) => {
  const id = prescription?.id ?? prescription?.prescriptionId;
  if (id == null || id === "") return null;
  const numeric = Number(id);
  return Number.isNaN(numeric) ? id : numeric;
};

const getReportPrescriptionId = (report) => {
  const id =
    report?.prescriptionId ??
    report?.prescription_id ??
    report?.patientPathology?.prescriptionId ??
    report?.patient_pathology?.prescription_id;
  if (id == null || id === "") return null;
  const numeric = Number(id);
  return Number.isNaN(numeric) ? id : numeric;
};

const getReportAppointmentId = (report) => {
  const id = report?.appointmentId ?? report?.appointment_id;
  if (id == null || id === "") return null;
  const numeric = Number(id);
  return Number.isNaN(numeric) ? id : numeric;
};

/** Normalize path-lab rows for patient dashboard lists and appointment matching. */
export const processPatientLabReports = (testsRaw) => {
  const tests = dedupePathLabReportRecords(
    Array.isArray(testsRaw) ? testsRaw : testsRaw ? [testsRaw] : []
  );

  const processedTests = tests.map((test) => {
    const testDate =
      test?.reportDate || test?.createdAt || test?.created_at || test?.testDate;

    let status = test?.resultStatus || test?.status || null;
    const statusUpper = status ? String(status).toUpperCase() : "";

    const hasResult =
      test?.result !== null &&
      test?.result !== undefined &&
      test?.result !== "" &&
      String(test?.result).trim() !== "" &&
      String(test?.result).toLowerCase() !== "null";

    if (hasResult) {
      if (statusUpper === "REJECT" || statusUpper === "REJECTED") {
        status = "REJECTED";
      } else {
        status = "COMPLETED";
      }
    } else {
      status = statusUpper || "PENDING";
    }

    return {
      id: test?.id || test?.reportId || `${test?.testId}-${testDate}`,
      reportId: test?.reportId ?? test?.id ?? null,
      testName:
        test?.testName ||
        test?.test_name ||
        test?.name ||
        test?.labTestName ||
        "Unknown Test",
      status,
      result: test?.result || null,
      normalRange: test?.normalRange || null,
      reportDate: testDate,
      date: testDate ? dayjs(testDate) : null,
      prescriptionId: getReportPrescriptionId(test),
      appointmentId: getReportAppointmentId(test),
      testId: test?.testId ?? test?.labTestId ?? test?.lab_test_id ?? null,
      patientId: test?.patientId ?? test?.patient_id ?? null,
      doctorId: test?.doctorId ?? test?.doctor_id ?? null,
      doctorName: test?.doctorName || test?.assignedByName || null,
      resultStatus: test?.resultStatus || test?.status || null,
    };
  });

  processedTests.sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;
    return b.date.valueOf() - a.date.valueOf();
  });

  return dedupePatientLabReportRows(processedTests);
};

export const matchPrescriptionsToAppointment = (prescriptions, enriched) => {
  const appointmentId = getAppointmentIdFromEnriched(enriched);
  const list = Array.isArray(prescriptions) ? prescriptions : [];

  if (appointmentId != null) {
    const linked = list.filter(
      (p) => getPrescriptionAppointmentId(p) === appointmentId
    );
    if (linked.length > 0) return linked;
  }

  const doctorId = enriched?.doctorId || enriched?.appointmentData?.doctorId;
  const aptDate = enriched?.when;
  if (!doctorId || !aptDate?.isValid?.()) return [];

  return list.filter((p) => {
    if (getPrescriptionAppointmentId(p) != null) return false;
    const pDoctorId = p.doctorId || p.doctor_id;
    if (Number(pDoctorId) !== Number(doctorId)) return false;
    const pDate = dayjs(p.createdAt || p.prescriptionDate || p.date);
    return pDate.isValid() && pDate.isSame(aptDate, "day");
  });
};

/** True when the consultation has occurred or is marked complete. */
const isConsultationFinished = (enriched) => {
  const data = enriched?.appointmentData || enriched;
  if (isAppointmentCompleted(data)) return true;
  const when = enriched?.when;
  return Boolean(when?.isValid?.() && !when.isAfter(dayjs()));
};

export const matchLabReportsToAppointment = (
  labReports,
  enriched,
  matchedPrescriptions = []
) => {
  const appointmentId = getAppointmentIdFromEnriched(enriched);
  const reports = Array.isArray(labReports) ? labReports : [];
  const prescriptions = Array.isArray(matchedPrescriptions)
    ? matchedPrescriptions
    : [];

  if (appointmentId != null) {
    const byAppointment = reports.filter(
      (r) => getReportAppointmentId(r) === appointmentId
    );
    if (byAppointment.length > 0) return byAppointment;
  }

  if (!isConsultationFinished(enriched)) {
    return [];
  }

  const prescriptionIds = new Set(
    prescriptions.map((p) => getPrescriptionId(p)).filter((id) => id != null)
  );

  if (prescriptionIds.size > 0) {
    const byPrescription = reports.filter((r) => {
      const reportPrescriptionId = getReportPrescriptionId(r);
      return (
        reportPrescriptionId != null && prescriptionIds.has(reportPrescriptionId)
      );
    });
    if (byPrescription.length > 0) return byPrescription;
  }

  const aptDate = enriched?.when;
  if (!aptDate?.isValid?.() || prescriptionIds.size === 0) return [];

  const doctorId = enriched?.doctorId || enriched?.appointmentData?.doctorId;

  return reports.filter((r) => {
    const reportDate = r.date?.isValid?.() ? r.date : dayjs(r.reportDate);
    if (!reportDate?.isValid?.()) return false;
    if (!reportDate.isSame(aptDate, "day")) return false;

    const reportDoctorId = r.doctorId ?? r.doctor_id;
    if (doctorId != null && reportDoctorId != null) {
      return Number(reportDoctorId) === Number(doctorId);
    }
    return true;
  });
};

const formatPaymentModeLabel = (modeUpper) => {
  if (!modeUpper) return null;
  if (modeUpper === "ONLINE" || modeUpper === "PAID_ONLINE") return "Paid Online";
  if (modeUpper === "CASH") return "Paid (Cash)";
  if (modeUpper === "CREDIT") return "Credit";
  if (modeUpper === "UPI") return "Paid (UPI)";
  if (modeUpper === "CARD") return "Paid (Card)";
  if (modeUpper === "PAY_AT_HOSPITAL") return "Pay at Hospital";
  const title =
    modeUpper.charAt(0) + modeUpper.slice(1).toLowerCase().replace(/_/g, " ");
  return `Paid (${title})`;
};

/** Map appointment id → invoice row when the API links them. */
export const buildInvoicePaymentLookup = (invoicesRaw) => {
  const map = new Map();
  const invoices = Array.isArray(invoicesRaw) ? invoicesRaw : [];
  invoices.forEach((inv) => {
    const aptId = inv?.appointmentId ?? inv?.appointment_id;
    if (aptId == null || aptId === "") return;
    const key = Number(aptId);
    if (!Number.isNaN(key)) map.set(key, inv);
  });
  return map;
};

export const getAppointmentInvoicePaymentMode = (appointmentId, invoiceLookup) => {
  if (appointmentId == null || !invoiceLookup) return null;
  const inv = invoiceLookup.get(Number(appointmentId));
  return inv?.paymentMode || inv?.payment_mode || null;
};

/** Resolve payment label for patient dashboard appointment view. */
export const formatPaymentLabel = (enriched) => {
  const data = enriched?.appointmentData || enriched;

  const methodUpper = getPaymentMethodUpper(data);
  if (methodUpper === "PAY_AT_HOSPITAL") {
    const status = getAppointmentStatusUpper(data);
    if (
      hasAppointmentToken(data) ||
      status === "CONFIRMED" ||
      status === "COMPLETED"
    ) {
      return "Paid at Hospital";
    }
    return "Pay at Hospital";
  }
  if (methodUpper === "PAID_ONLINE") return "Paid Online";

  const modeUpper = String(
    enriched?.paymentMode ||
      data?.paymentMode ||
      data?.payment_mode ||
      data?.invoice?.paymentMode ||
      data?.invoice?.payment_mode ||
      ""
  ).toUpperCase();
  const modeLabel = formatPaymentModeLabel(modeUpper);
  if (modeLabel) return modeLabel;

  const status = getAppointmentStatusUpper(data);
  if (hasAppointmentToken(data)) {
    if (status === "PENDING_CONFIRMATION" || status === "PENDING") {
      return "Pay at Hospital";
    }
    return "Paid at Hospital";
  }

  if (status === "CONFIRMED" || status === "COMPLETED") {
    return "Paid at Hospital";
  }

  if (needsFrontdeskConfirmation(data)) {
    return "Pay at Hospital";
  }

  if (enriched?.isVirtual) {
    return "Paid Online";
  }

  return methodUpper || "—";
};

export const normalizePrescriptionTextEntry = (entry) => {
  if (typeof entry === "string") return entry.trim();
  if (entry && typeof entry === "object") {
    return String(
      entry.complaint ||
        entry.diagnosis ||
        entry.complaintName ||
        entry.diagnosisName ||
        entry.name ||
        entry.value ||
        ""
    ).trim();
  }
  return String(entry ?? "").trim();
};

export const getPrescriptionComplaintsList = (prescription) =>
  (Array.isArray(prescription?.complaints) ? prescription.complaints : [])
    .map(normalizePrescriptionTextEntry)
    .filter(Boolean);

export const getShortComplaintsText = (prescription, maxLen = 72) => {
  const text = getPrescriptionComplaintsList(prescription).join(", ");
  if (!text) return "—";
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen - 1)}…`;
};

export const getPrescriptionDisplayId = (prescription) =>
  prescription?.id ?? prescription?.prescriptionId ?? prescription?.prescription_id ?? "—";

export const getPrescriptionDisplayDate = (prescription) => {
  const raw =
    prescription?.createdAt ||
    prescription?.prescriptionDate ||
    prescription?.date ||
    prescription?.updatedAt;
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : "—";
};

export const getPrescriptionDoctorName = (prescription, doctorLookup = {}) => {
  const doctorId = prescription?.doctorId ?? prescription?.doctor_id;
  const fromLookup =
    doctorId != null
      ? doctorLookup[doctorId] || doctorLookup[String(doctorId)] || doctorLookup[Number(doctorId)]
      : null;
  return (
    prescription?.doctorName ||
    fromLookup?.name ||
    prescription?.doctor?.name ||
    "Doctor"
  );
};

export const getLabTestListStatusLabel = (test) => {
  const statusUpper = String(test?.status || test?.resultStatus || "").toUpperCase();
  const hasResult =
    test?.result !== null &&
    test?.result !== undefined &&
    String(test.result).trim() !== "" &&
    String(test.result).toLowerCase() !== "null";

  if (
    hasResult ||
    statusUpper === "COMPLETED" ||
    statusUpper === "COMPLETE" ||
    statusUpper === "BILLED"
  ) {
    return "Completed";
  }
  return "Scheduled";
};

const parseDashboardLabDate = (value) => {
  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day, hour = 0, minute = 0] = value;
    const parsed = dayjs(new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute)));
    return parsed.isValid() ? parsed : null;
  }
  if (value == null || value === "") return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const buildPrescriptionLabNameLookup = (prescriptions) => {
  const byKey = new Map();
  (Array.isArray(prescriptions) ? prescriptions : []).forEach((prescription) => {
    const prescriptionId =
      prescription?.id ?? prescription?.prescriptionId ?? prescription?.prescription_id;
    (Array.isArray(prescription?.patientLabTests) ? prescription.patientLabTests : []).forEach((test) => {
      const labTestId = test?.labTestId ?? test?.lab_test_id ?? test?.id;
      const name = test?.testName || test?.labTestName || test?.name;
      if (!name || labTestId == null) return;
      byKey.set(`${prescriptionId ?? ""}:${labTestId}`, name);
      if (!byKey.has(String(labTestId))) byKey.set(String(labTestId), name);
    });
  });
  return byKey;
};

const flattenPatientLabBookings = (bookings) => {
  const rows = [];
  (Array.isArray(bookings) ? bookings : []).forEach((booking) => {
    const nested =
      booking?.labTests ||
      booking?.patientLabTests ||
      booking?.tests ||
      booking?.bookedTests;
    if (Array.isArray(nested) && nested.length > 0) {
      nested.forEach((test, index) => {
        rows.push({
          ...booking,
          ...test,
          id: test?.id ?? `${booking?.id ?? "booking"}-${index}`,
          prescriptionId: test?.prescriptionId ?? test?.prescription_id ?? booking?.prescriptionId ?? booking?.prescription_id,
          preferredDate: test?.preferredDate ?? booking?.preferredDate ?? booking?.preferred_date,
          preferredTime: test?.preferredTime ?? booking?.preferredTime ?? booking?.preferred_time,
          doctorId: test?.doctorId ?? test?.doctor_id ?? booking?.doctorId ?? booking?.doctor_id,
          doctorName: test?.doctorName ?? booking?.doctorName,
        });
      });
      return;
    }
    rows.push(booking);
  });
  return rows;
};

const readBookingPreferredTime = (booking) => {
  const direct =
    booking?.preferredTime ||
    booking?.preferred_time ||
    booking?.appointmentTime ||
    booking?.slotTime ||
    "";
  if (direct) return String(direct).trim();
  const notes = String(booking?.notes || "");
  const match = notes.match(/(?:Preferred time:| at )\s*(\d{1,2}:\d{2}\s*[AP]M)/i);
  return match?.[1]?.trim() || "";
};

const readBookingPreferredDate = (booking) => {
  const rawDate =
    booking?.preferredDate ||
    booking?.preferred_date ||
    booking?.bookingDate ||
    booking?.booking_date ||
    null;
  const parsed = parseDashboardLabDate(rawDate);
  if (parsed) return { rawDate, date: parsed };
  const notes = String(booking?.notes || "");
  const match = notes.match(/on\s+(\d{4}-\d{2}-\d{2})/i);
  if (!match) return { rawDate: null, date: null };
  return { rawDate: match[1], date: parseDashboardLabDate(match[1]) };
};

const getDashboardLabIdentity = (row) => {
  const nestedTest = row?.labTest || row?.lab_test || row?.test || null;
  const testId =
    row?.testId ??
    row?.labTestId ??
    row?.lab_test_id ??
    nestedTest?.id ??
    nestedTest?.labTestId ??
    null;
  const prescriptionId = row?.prescriptionId ?? row?.prescription_id ?? null;
  const testName = String(row?.testName || row?.labTestName || row?.name || "").trim().toLowerCase();
  return {
    testId,
    prescriptionId,
    identityKey: `${prescriptionId ?? ""}:${testId ?? ""}`,
    nameKey: `${prescriptionId ?? ""}:${testId ?? ""}:${testName}`,
  };
};

export const mergePatientDashboardLabTests = (processedReports, bookings, prescriptions = []) => {
  const reports = Array.isArray(processedReports) ? processedReports : [];
  const bookingList = flattenPatientLabBookings(bookings);
  const nameLookup = buildPrescriptionLabNameLookup(prescriptions);
  const patientBookingByKey = new Map();

  bookingList.forEach((booking) => {
    const { testId, prescriptionId, identityKey, nameKey } = getDashboardLabIdentity(booking);
    const lookedUpName =
      nameLookup.get(`${prescriptionId ?? ""}:${testId ?? ""}`) ||
      (testId != null ? nameLookup.get(String(testId)) : "");
    const testName = String(
      booking?.testName || booking?.labTestName || booking?.name || lookedUpName || ""
    )
      .trim()
      .toLowerCase();
    const preferred = readBookingPreferredDate(booking);
    const info = {
      preferredTime: readBookingPreferredTime(booking),
      rawDate: preferred.rawDate,
      date: preferred.date,
    };
    if (testId != null) patientBookingByKey.set(identityKey, info);
    patientBookingByKey.set(nameKey, info);
    if (testName) patientBookingByKey.set(`${prescriptionId ?? ""}:${testName}`, info);
  });

  const findPatientBooking = (row) => {
    const { testId, prescriptionId, identityKey, nameKey } = getDashboardLabIdentity(row);
    if (testId != null && patientBookingByKey.has(identityKey)) {
      return patientBookingByKey.get(identityKey);
    }
    if (patientBookingByKey.has(nameKey)) return patientBookingByKey.get(nameKey);
    const testName = String(row?.testName || row?.labTestName || row?.name || "")
      .trim()
      .toLowerCase();
    if (!testName) return null;
    return patientBookingByKey.get(`${prescriptionId ?? ""}:${testName}`) || null;
  };

  const markedReports = reports.map((report) => {
    const patientBooking = findPatientBooking(report);
    if (!patientBooking) return report;
    return {
      ...report,
      bookedByPatient: true,
      date: patientBooking.date || report.date,
      reportDate: patientBooking.rawDate || report.reportDate,
      preferredTime: patientBooking.preferredTime || "",
    };
  });

  const extraFromBookings = bookingList
    .map((booking) => {
      const nestedTest = booking?.labTest || booking?.lab_test || booking?.test || null;
      const { testId, prescriptionId, identityKey } = getDashboardLabIdentity(booking);
      const lookedUpName =
        nameLookup.get(`${prescriptionId ?? ""}:${testId ?? ""}`) ||
        (testId != null ? nameLookup.get(String(testId)) : "");
      const testName =
        booking?.testName ||
        booking?.labTestName ||
        booking?.name ||
        nestedTest?.testName ||
        nestedTest?.labTestName ||
        nestedTest?.name ||
        lookedUpName ||
        getLabTestDisplayName(booking) ||
        (testId != null ? `Lab Test #${testId}` : "Lab Test");
      const namedKey = `${identityKey}:${String(testName).trim().toLowerCase()}`;
      const alreadyShown = markedReports.some((report) => {
        const reportIdentity = getDashboardLabIdentity(report);
        if (testId != null && reportIdentity.testId != null) {
          return reportIdentity.identityKey === identityKey;
        }
        return reportIdentity.nameKey === namedKey;
      });
      if (alreadyShown) return null;

      const preferred = readBookingPreferredDate(booking);
      const rawDate = preferred.rawDate;
      const parsedDate = preferred.date;
      const preferredTime = readBookingPreferredTime(booking);
      const bookingStatus = String(booking?.status || booking?.resultStatus || "").toUpperCase();
      const hasResult =
        booking?.result !== null &&
        booking?.result !== undefined &&
        String(booking.result).trim() !== "";

      return {
        id: booking?.id != null ? `booking-${booking.id}-${testId ?? testName}` : namedKey,
        bookingId: booking?.id ?? null,
        reportId: booking?.reportId ?? null,
        testName,
        bookedByPatient: true,
        status: hasResult || bookingStatus === "COMPLETED" || bookingStatus === "COMPLETE"
          ? "COMPLETED"
          : "SCHEDULED",
        result: booking?.result || null,
        normalRange: booking?.normalRange || null,
        reportDate: rawDate,
        date: parsedDate,
        preferredTime,
        prescriptionId,
        appointmentId: null,
        testId,
        patientId: booking?.patientId ?? booking?.patient_id ?? null,
        doctorId: booking?.doctorId ?? booking?.doctor_id ?? null,
        doctorName: booking?.doctorName || null,
        resultStatus: booking?.resultStatus || booking?.status || "SCHEDULED",
      };
    })
    .filter(Boolean);

  return [...markedReports, ...extraFromBookings].sort((a, b) => {
    const aTime = a?.date?.isValid?.() ? a.date.valueOf() : 0;
    const bTime = b?.date?.isValid?.() ? b.date.valueOf() : 0;
    return bTime - aTime;
  });
};

export const buildPrescriptionOpdPrintDayData = (prescription, appointment = null) => {
  const medicines = Array.isArray(prescription?.medicines)
    ? prescription.medicines
    : Array.isArray(prescription?.medicine)
    ? prescription.medicine
    : [];
  const diagnoses = (Array.isArray(prescription?.diagnoses) ? prescription.diagnoses : [])
    .map(normalizePrescriptionTextEntry)
    .filter(Boolean);
  const labTests = (Array.isArray(prescription?.patientLabTests)
    ? prescription.patientLabTests
    : []
  ).map((test) => ({
    testName:
      test?.testName ||
      test?.labTestName ||
      test?.name ||
      (test?.labTestId != null ? `Lab Test #${test.labTestId}` : "Lab Test"),
  }));
  const visitDateRaw =
    appointment?.when?.isValid?.()
      ? appointment.when
      : dayjs(
          appointment?.appointmentDateTime ||
            appointment?.appointmentDate ||
            prescription?.createdAt ||
            prescription?.prescriptionDate
        );

  return {
    prescriptions: [
      {
        ...prescription,
        id: prescription?.id || prescription?.prescriptionId,
        medicines: medicines.map((med) => ({
          ...med,
          name: med.name || med.medicineName || med.medicine || "Medicine",
        })),
        doctorName:
          prescription?.doctorName || appointment?.doctorName || "Doctor",
        advice:
          prescription?.advice ||
          prescription?.notes ||
          prescription?.medicineNotes ||
          "",
        followUpDate:
          prescription?.followUpDate || prescription?.nextVisitDate || "",
      },
    ],
    appointments: appointment
      ? [
          {
            ...appointment,
            doctorName: appointment.doctorName || prescription?.doctorName || "",
            appointmentDateTime: visitDateRaw?.isValid?.()
              ? visitDateRaw.format("DD MMM YYYY, hh:mm A")
              : appointment.appointmentDateTime || appointment.appointmentDate,
          },
        ]
      : [],
    diagnoses,
    complaints: getPrescriptionComplaintsList(prescription),
    vitalSigns: Array.isArray(prescription?.vitalSigns)
      ? prescription.vitalSigns
      : prescription?.vitalSigns
      ? [prescription.vitalSigns]
      : [],
    LabtestList: labTests,
  };
};

